import { FetchRequestState } from 'types'
import { InformalFeedbackRequest, InformalFeedbackRequestState } from 'api/clients/InformalFeedbackRequestsClient'
import { useInformalFeedbackRequests as useInformalFeedbackRequestsQuery } from 'hooks/queries/informalFeedbackRequests/AlmanacFiles/useInformalFeedbackRequests'
import { QueryObserverResult } from 'react-query/types/core/types'
import { useAuthOrUnregisteredUser } from 'hooks/useAuthOrUnregisteredUser'

export interface ArticleInformalFeedbackRequests {
  informalFeedbackRequests: InformalFeedbackRequest[]
  requestedByMe: InformalFeedbackRequest[]
  requestedByOthers: InformalFeedbackRequest[]
  mostRecentClosedRequest?: InformalFeedbackRequest
  openRequest?: InformalFeedbackRequest
  loadingState: FetchRequestState
}

const loadingStateFromQuery = ({ isError, isLoading, isSuccess }: QueryObserverResult) => {
  if (isLoading) {
    return FetchRequestState.Loading
  }

  if (isError) {
    return FetchRequestState.Rejected
  }

  if (isSuccess) {
    return FetchRequestState.Resolved
  }

  return FetchRequestState.Idle
}

export const useInformalFeedbackRequests = (
  almanacFileId: string,
  options: {
    staleTime?: number
    states?: InformalFeedbackRequestState[]
    enabled?: boolean
  } = { enabled: true },
): ArticleInformalFeedbackRequests & QueryObserverResult => {
  const userId = useAuthOrUnregisteredUser().user?.id
  const informalFeedbackRequestsQueryResult = useInformalFeedbackRequestsQuery({
    almanacFileId,
    states: [InformalFeedbackRequestState.Open, InformalFeedbackRequestState.Closed],
    ...options,
  })
  const queryResult = informalFeedbackRequestsQueryResult
  const { data } = queryResult
  const informalFeedbackRequests =
    data?.informal_feedback_requests.filter((request) => {
      return (
        request.state === InformalFeedbackRequestState.Open ||
        (request.state === InformalFeedbackRequestState.Closed &&
          request.requester.id === userId &&
          !request.cleanup_dismissed)
      )
    }) || []
  const loadingState = loadingStateFromQuery(informalFeedbackRequestsQueryResult)

  const requestedByMe = informalFeedbackRequests.filter(({ requester }) => requester.id === userId)
  const requestedByOthers = informalFeedbackRequests.filter(({ requester }) => requester.id !== userId)
  const mostRecentClosedRequest = data?.informal_feedback_requests.find((request) => {
    return request.state === InformalFeedbackRequestState.Closed
  })
  const closedRequest = mostRecentClosedRequest?.cleanup_dismissed ? undefined : mostRecentClosedRequest
  const openRequest = data?.informal_feedback_requests.find((request) => {
    return request.state === InformalFeedbackRequestState.Open
  })

  return {
    requestedByMe,
    requestedByOthers,
    informalFeedbackRequests,
    loadingState,
    mostRecentClosedRequest: closedRequest,
    openRequest,
    ...queryResult,
  }
}
